import _isNaN from 'lodash/isNaN';
import _throttle from 'lodash/throttle';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import {
  requestVideoPlayerCurrentTime,
  updateVideoPlayerHideReaction,
  updateVideoPlayerLocalReactions,
  updateVideoPlayerSeeking,
} from 'actions/videoPlayer';
import { getNewTimeSkipRanges } from 'utilities/trimming';

import { selectIsActive } from 'selectors/viewerInsights';

import AddTrimRangeTooltip from './add-trim-range-tooltip';
import CommentBar from './comment-bar';
import EmojiTimeline from './emoji-timeline';
import LoadProgressBar from './load-progress-bar';
import PlayProgressBar from './play-progress-bar';
import PlayProgressScrubber from './play-progress-scrubber';
import TrimRangeEditBar from './trim-range-edit-bar';
import TrimRangesBar from './trim-ranges-bar';

import { ViewersInsightsTimeline } from 'components/share-video/viewer-insights/list';
import Slider from 'components/slider';

import './styles.less';

class SeekBar extends Component {
  state = {
    play_after_seek_end: false,
  };

  render() {
    const {
      controlBarExpanded,
      currentTime,
      currentTrimRange,
      duration,
      fullScreen,
      inTrimMode,
      isMainVideoOnSharePage,
      localReactions,
      reactions,
      selectedTrimRange,
      useEmojis,
      isViewerInsightsActive,
    } = this.props;

    const progressAsAPercentage = (this.getPercent() * 100).toFixed(2);

    return (
      <div
        ref={ref => (this.container = ref)}
        className={`video-player-seek-bar ${inTrimMode ? 'in-trim-mode' : ''}`}
      >
        {/*
          we want to scale the backgroud (non-loaded content) on hover but not
          the child elements
        */}
        <Slider
          ref={slider => (this.slider = slider)}
          getPercent={this.getPercent}
          label={'Video Progress'}
          onBlur={this.onMouseUp}
          onMouseDown={this.onMouseDown}
          onMouseMove={this.onMouseMove}
          onMouseUp={this.onMouseUp}
          valueNow={progressAsAPercentage}
        >
          <LoadProgressBar duration={duration} />
          <PlayProgressBar currentTime={currentTime} inTrimMode={inTrimMode} />
          {isMainVideoOnSharePage &&
            !inTrimMode &&
            !fullScreen &&
            !isViewerInsightsActive && <CommentBar duration={duration} />}
          {isMainVideoOnSharePage && inTrimMode && (
            <TrimRangesBar duration={duration} />
          )}
          {isMainVideoOnSharePage && inTrimMode && currentTrimRange != null && (
            <TrimRangeEditBar
              getNewTime={this.getNewTime}
              onMouseDown={this.onMouseDown}
              onMouseMove={this.onMouseMove}
              onMouseUp={this.onMouseUp}
            />
          )}
          <PlayProgressScrubber controlBarExpanded={controlBarExpanded} />
          {!inTrimMode && !isViewerInsightsActive && useEmojis && (
            <EmojiTimeline
              reactions={reactions}
              localReactions={localReactions}
              controlBarExpanded={controlBarExpanded}
              containerDimensions={this.props.containerDimensions}
              animateReactions={this.props.animateReactions}
              currentTime={currentTime}
              duration={duration}
            />
          )}
          <ViewersInsightsTimeline />
        </Slider>
        <div className="seek-bar-background" />
        {isMainVideoOnSharePage &&
          inTrimMode &&
          currentTrimRange == null &&
          selectedTrimRange == null && (
            <AddTrimRangeTooltip getPercent={this.getPercent} />
          )}
      </div>
    );
  }

  componentDidMount() {
    window.addEventListener('resize', this.onWindowResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  }

  // throttled to fire once every 100ms
  onWindowResize = _throttle(() => {
    this.forceUpdate();
  }, 100);

  getNewTime = e => {
    const { currentTime, duration } = this.props;

    // onBlur causes component unmount and ref change to null
    if (!this.slider) {
      return currentTime;
    }

    const distance = this.slider.calculateDistance(e);
    const newTime = distance * duration;

    if (_isNaN(newTime)) {
      return currentTime;
    }

    // don't let video end while scrubbing
    return Math.min(newTime, duration - 0.01);
  };

  changeTime(e) {
    const {
      currentTime,
      currentTrimRange,
      duration,
      inTrimMode,
      trimRanges,
    } = this.props;

    let newTime = this.getNewTime(e);

    // skip over trim ranges
    if (inTrimMode && currentTrimRange == null) {
      newTime =
        getNewTimeSkipRanges(
          Math.round(currentTime * 1000),
          Math.round(newTime * 1000),
          Math.round(duration * 1000),
          trimRanges,
          currentTrimRange,
          true
        ) / 1000;
    }

    this.props.requestVideoPlayerCurrentTime(newTime);
  }

  onMouseDown = () => {
    document.addEventListener('mouseup', this.onMouseUp, false);

    const { paused } = this.props;

    this.setState({ play_after_seek_end: !paused });
  };

  onMouseMove = e => {
    this.changeTime(e);
    this.props.updateVideoPlayerSeeking(true);

    const { paused } = this.props;

    if (!paused) {
      this.props.onPause();
    }
  };

  onMouseUp = e => {
    document.removeEventListener('mouseup', this.onMouseUp);

    this.changeTime(e);
    this.props.updateVideoPlayerSeeking(false);

    if (this.state.play_after_seek_end) {
      this.props.onPlay();
    }

    this.setState({ play_after_seek_end: false });
  };

  getPercent = () => {
    const { currentTime, duration } = this.props;

    return Math.min(currentTime / duration, 1);
  };
}

const mapStateToProps = state => {
  const {
    current_trim_range: currentTrimRange,
    in_trim_mode: inTrimMode,
    selected_trim_range: selectedTrimRange,
    trim_ranges: trimRanges,
  } = state.trimming;
  const {
    reactions: reactions,
    local_reactions: localReactions,
    video: { use_emojis: useEmojis },
    hide_reaction: hideReaction,
    animate_reactions: animateReactions,
  } = state.video_player;

  return {
    currentTrimRange,
    inTrimMode,
    selectedTrimRange,
    trimRanges,
    localReactions,
    reactions,
    hideReaction,
    animateReactions,
    useEmojis,
    isViewerInsightsActive: selectIsActive(state),
  };
};

const mapDispatchToProps = dispatch => {
  return {
    requestVideoPlayerCurrentTime: currentTime =>
      dispatch(requestVideoPlayerCurrentTime(currentTime)),
    updateVideoPlayerSeeking: seeking =>
      dispatch(updateVideoPlayerSeeking(seeking)),

    // reactions
    updateVideoPlayerLocalReactions: reactions =>
      dispatch(updateVideoPlayerLocalReactions(reactions)),
    onHideReaction: reaction =>
      dispatch(updateVideoPlayerHideReaction(reaction)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SeekBar);
